import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
} from 'react';

import { DialogShell } from '../../../components/DialogShell';
import { SelectiveImportDialog } from '../../../components/SelectiveImportDialog';
import { SpreadsheetImportDialog } from '../../../components/SpreadsheetImportDialog';
import type { EpsgSearchResult } from '../../../crs/epsgCatalogue';
import type { ImportedVectorData } from '../../../data/import/model';
import { importDataFile, type PendingTabularImport } from '../../../data/import/dataImport';
import type { ImportProgress, ImportSubsetResult } from '../../../data/import/importPreflight';
import type {
  PendingShapefileImport,
  ShapefileCrsResolutionResult,
} from '../../../data/import/shapefileImport';
import type { ShapefileSelectiveSession } from '../../../data/import/shapefilePreflight';
import {
  inspectLayerReplacement,
  type ReplacementFieldResolution,
  type ReplacementFieldResolutions,
} from '../../../data/replacement';
import type { VectorSourceDescriptor } from '../../../data/model';
import { OperationCancelledError } from '../../../operations/longOperation';
import { LatestOperationCoordinator } from '../../../operations/latestOperation';
import type { LayerDefinition } from '../../../project/model';
import { useAppStore } from '../../state/useAppStore';

interface ReplaceLayerDataDialogProps {
  readonly layer: LayerDefinition;
  readonly source: VectorSourceDescriptor;
  readonly onClose: () => void;
  readonly returnFocusTo?: HTMLElement | null | undefined;
}

interface OperationValue<T> {
  readonly operationId: number;
  readonly value: T;
}

function fieldTypeLabel(type: string): string {
  return type === 'integer' || type === 'number'
    ? 'number'
    : type === 'boolean'
      ? 'true/false'
      : type;
}

function familyLabel(families: readonly string[]): string {
  return families.length === 0 ? 'no renderable geometry' : families.join(', ');
}

function dependencyContext(areas: readonly string[], joinNames: readonly string[]): string {
  const labels = areas.map((area) =>
    area === 'Join key' && joinNames.length > 0 ? `${area} (${joinNames.join(', ')})` : area,
  );
  return labels.join(', ');
}

function removalConsequence(areas: readonly string[]): string {
  const consequences: string[] = [];
  if (areas.includes('Categorised styling') || areas.includes('Graduated styling')) {
    consequences.push('reset thematic styling to Single');
  }
  if (areas.includes('Label field')) consequences.push('turn labels off');
  else if (areas.includes('Label filter')) consequences.push('clear the label filter');
  if (areas.includes('Popup title') || areas.includes('Popup fields')) {
    consequences.push('remove the affected popup fields');
  }
  if (areas.includes('Display filter')) consequences.push('clear the display filter');
  if (areas.includes('Join key')) {
    consequences.push('remove the affected join and settings that depend on its joined fields');
  }
  return consequences.join(', ');
}

export function ReplaceLayerDataDialog({
  layer,
  source,
  onClose,
  returnFocusTo,
}: ReplaceLayerDataDialogProps) {
  const projectSources = useAppStore((state) => state.project.sources);
  const replaceLayerData = useAppStore((state) => state.replaceLayerData);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const chooseButtonRef = useRef<HTMLButtonElement | null>(null);
  const crsSearchRef = useRef<HTMLInputElement | null>(null);
  const [coordinator] = useState(() => new LatestOperationCoordinator());
  const [candidate, setCandidate] = useState<ImportedVectorData | null>(null);
  const [pendingTabular, setPendingTabular] = useState<OperationValue<PendingTabularImport> | null>(
    null,
  );
  const [pendingShapefile, setPendingShapefile] =
    useState<OperationValue<PendingShapefileImport> | null>(null);
  const [selectiveSession, setSelectiveSession] =
    useState<OperationValue<ShapefileSelectiveSession> | null>(null);
  const [resolutions, setResolutions] = useState<ReplacementFieldResolutions>({});
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [crsQuery, setCrsQuery] = useState('');
  const [crsResults, setCrsResults] = useState<readonly EpsgSearchResult[]>([]);
  const [selectedEpsgCode, setSelectedEpsgCode] = useState<number | null>(null);
  const [isSearchingCrs, setIsSearchingCrs] = useState(false);
  const titleId = useId();

  useEffect(() => () => coordinator.dispose(), [coordinator]);
  useEffect(() => () => pendingTabular?.value.workbook?.dispose(), [pendingTabular]);

  useEffect(() => {
    if (!pendingShapefile || crsQuery.trim().length < 2) return;
    const operationId = pendingShapefile.operationId;
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      void import('../../../crs/epsgCatalogue')
        .then(({ searchEpsgCatalogue }) => {
          if (!cancelled && coordinator.isCurrent(operationId)) {
            setCrsResults(searchEpsgCatalogue(crsQuery));
          }
        })
        .catch(() => {
          if (!cancelled && coordinator.isCurrent(operationId)) {
            setErrorMessage('Placeio Maps could not load the offline CRS catalogue. Try again.');
          }
        })
        .finally(() => {
          if (!cancelled && coordinator.isCurrent(operationId)) setIsSearchingCrs(false);
        });
    }, 150);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [coordinator, crsQuery, pendingShapefile]);

  const review = useMemo(
    () => (candidate ? inspectLayerReplacement(projectSources, layer, source, candidate) : null),
    [candidate, layer, projectSources, source],
  );

  const acceptCandidate = (imported: ImportedVectorData, operationId: number) => {
    if (!coordinator.isCurrent(operationId)) return;
    pendingTabular?.value.workbook?.dispose();
    setPendingTabular(null);
    setPendingShapefile(null);
    setSelectiveSession(null);
    setResolutions({});
    setCandidate(imported);
    setBusy(false);
    setStatus('');
    setErrorMessage(null);
    coordinator.finish(operationId);
  };

  const inspectFile = async (file: File) => {
    const operation = coordinator.start();
    pendingTabular?.value.workbook?.dispose();
    setPendingTabular(null);
    setPendingShapefile(null);
    setSelectiveSession(null);
    setResolutions({});
    setCandidate(null);
    setErrorMessage(null);
    setBusy(true);
    setStatus(
      /\.xlsx$/i.test(file.name)
        ? 'Inspecting workbook locally…'
        : 'Reading and validating data locally…',
    );
    try {
      const result = await importDataFile(file, {
        signal: operation.signal,
        onXlsxStage: (stage) => {
          if (!coordinator.isCurrent(operation.id)) return;
          setStatus(
            stage === 'loading-library'
              ? 'Starting spreadsheet worker…'
              : stage === 'reading-workbook'
                ? 'Reading workbook locally…'
                : 'Normalizing worksheet locally…',
          );
        },
      });
      if (!coordinator.isCurrent(operation.id)) {
        if (result.status === 'needs-tabular-intent') result.value.workbook?.dispose();
        return;
      }
      setBusy(false);
      setStatus('');
      if (result.status === 'error') {
        setErrorMessage(result.error.message);
        coordinator.finish(operation.id);
      } else if (result.status === 'needs-tabular-intent') {
        setPendingTabular({ operationId: operation.id, value: result.value });
      } else if (result.status === 'needs-crs') {
        setPendingShapefile({ operationId: operation.id, value: result.value });
      } else if (result.status === 'needs-subset') {
        setBusy(true);
        setStatus('Inspecting dataset locally…');
        const { preflightShapefileZip } = await import('../../../data/import/shapefilePreflight');
        const preflight = await preflightShapefileZip(result.value.file, {
          signal: operation.signal,
        });
        if (!coordinator.isCurrent(operation.id)) return;
        setBusy(false);
        setStatus('');
        if (preflight.ok) {
          setSelectiveSession({ operationId: operation.id, value: preflight.value });
        } else {
          setErrorMessage(preflight.error.message);
          coordinator.finish(operation.id);
        }
      } else if (result.status === 'imported-many') {
        if (result.values.length === 1) acceptCandidate(result.values[0]!, operation.id);
        else {
          setErrorMessage('Choose KML or KMZ with one dataset to replace this layer.');
          coordinator.finish(operation.id);
        }
      } else if (result.status === 'needs-geopackage') {
        result.value.dispose();
        setErrorMessage(
          'Replace Layer Data does not yet support choosing a GeoPackage table. Use a supported single-layer replacement file.',
        );
        coordinator.finish(operation.id);
      } else {
        acceptCandidate(result.value, operation.id);
      }
    } catch (error) {
      if (!coordinator.isCurrent(operation.id)) return;
      setBusy(false);
      setStatus('');
      setErrorMessage(
        error instanceof OperationCancelledError
          ? 'Replacement import cancelled.'
          : 'Placeio Maps could not read this replacement file. Try again.',
      );
      coordinator.finish(operation.id);
    }
  };

  const handleFileInput = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.item(0);
    event.currentTarget.value = '';
    if (file) void inspectFile(file);
  };

  const cancelWorkflow = () => {
    const currentId = coordinator.currentId;
    if (currentId !== null) coordinator.cancel(currentId);
    pendingTabular?.value.workbook?.dispose();
    onClose();
  };

  const resolveCrs = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!pendingShapefile || selectedEpsgCode === null) return;
    const { operationId, value } = pendingShapefile;
    setBusy(true);
    setErrorMessage(null);
    let result: ShapefileCrsResolutionResult;
    try {
      const { resolvePendingShapefileCrs } = await import('../../../data/import/shapefileImport');
      result = await resolvePendingShapefileCrs(value, { epsgCode: selectedEpsgCode });
    } catch {
      if (!coordinator.isCurrent(operationId)) return;
      setBusy(false);
      setErrorMessage(
        'Placeio Maps could not load the local CRS transformation module. Try again.',
      );
      return;
    }
    if (!coordinator.isCurrent(operationId)) return;
    setBusy(false);
    if (result.ok) acceptCandidate(result.value, operationId);
    else setErrorMessage(result.error.message);
  };

  const importSubset = async (
    subset: ImportSubsetResult,
    options: { readonly signal: AbortSignal; readonly onProgress: (value: ImportProgress) => void },
  ) => {
    if (!selectiveSession) return;
    const { operationId, value: session } = selectiveSession;
    const rawData = await session.importSubset(subset, options);
    options.signal.throwIfAborted();
    if (!coordinator.isCurrent(operationId)) return;
    options.onProgress({ phase: 'reprojecting' });
    const { finishParsedShapefile } = await import('../../../data/import/shapefileImport');
    const result = await finishParsedShapefile(
      session.archive.name,
      {
        datasetName: session.selected.datasetName,
        suggestedLayerName: session.selected.suggestedLayerName,
        componentFileNames: session.selected.componentFileNames,
        ...(session.prjText ? { prjText: session.prjText } : {}),
      },
      rawData,
    );
    options.signal.throwIfAborted();
    if (!coordinator.isCurrent(operationId)) return;
    if (result.status === 'error') throw new Error(result.error.message);
    if (result.status === 'needs-subset') throw new Error('Unexpected nested subset request.');
    setSelectiveSession(null);
    if (result.status === 'needs-crs') {
      setPendingShapefile({ operationId, value: result.value });
    } else {
      acceptCandidate(result.value, operationId);
    }
  };

  if (pendingTabular) {
    return (
      <SpreadsheetImportDialog
        onCancel={cancelWorkflow}
        onImportPoints={(imported) => acceptCandidate(imported, pendingTabular.operationId)}
        onImportTable={() => undefined}
        pending={pendingTabular.value}
        returnFocusTo={returnFocusTo}
        vectorOnly
      />
    );
  }

  if (selectiveSession) {
    return (
      <SelectiveImportDialog
        onClose={cancelWorkflow}
        onImport={importSubset}
        returnFocusTo={returnFocusTo}
        session={selectiveSession.value}
      />
    );
  }

  if (pendingShapefile) {
    return (
      <DialogShell
        className="crs-resolution"
        closeOnEscape={!busy}
        element="form"
        initialFocusRef={crsSearchRef}
        onClose={cancelWorkflow}
        onSubmit={resolveCrs}
        returnFocusTo={returnFocusTo}
        titleId={titleId}
      >
        <p className="eyebrow">Replacement source CRS required</p>
        <h2 id={titleId}>Choose the coordinate system</h2>
        <p role="status">{pendingShapefile.value.message}</p>
        <label>
          Search by EPSG code or CRS name
          <input
            aria-label="Search replacement coordinate systems"
            autoComplete="off"
            onChange={(event) => {
              const query = event.currentTarget.value;
              setCrsQuery(query);
              setCrsResults([]);
              setSelectedEpsgCode(null);
              setIsSearchingCrs(query.trim().length >= 2);
              setErrorMessage(null);
            }}
            placeholder="For example: 28356 or GDA94 MGA 56"
            ref={crsSearchRef}
            type="search"
            value={crsQuery}
          />
        </label>
        {isSearchingCrs ? <p role="status">Searching the offline CRS catalogue…</p> : null}
        {crsResults.length > 0 ? (
          <ul
            aria-label="Replacement coordinate system search results"
            className="crs-search-results"
          >
            {crsResults.map((result) => (
              <li key={result.code}>
                <label
                  className={result.availability === 'supported' ? '' : 'crs-result-unsupported'}
                >
                  <input
                    checked={selectedEpsgCode === result.code}
                    disabled={result.availability !== 'supported'}
                    name="replacement-source-crs"
                    onChange={() => setSelectedEpsgCode(result.code)}
                    type="radio"
                    value={result.code}
                  />
                  <span>
                    <strong>{result.identifier}</strong> — {result.name}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        ) : null}
        {errorMessage ? (
          <p className="import-error" role="alert">
            {errorMessage}
          </p>
        ) : null}
        <div className="crs-resolution-actions">
          <button disabled={busy} onClick={cancelWorkflow} type="button">
            Cancel
          </button>
          <button
            className="primary-action"
            disabled={busy || selectedEpsgCode === null}
            type="submit"
          >
            Continue
          </button>
        </div>
      </DialogShell>
    );
  }

  const allResolved =
    review?.requiredResolutions.every((dependency) => Boolean(resolutions[dependency.field])) ??
    false;
  const setResolution = (field: string, resolution: ReplacementFieldResolution | null) => {
    setResolutions((current) => {
      const next = { ...current };
      if (resolution) next[field] = resolution;
      else delete next[field];
      return next;
    });
    setErrorMessage(null);
  };

  const confirmReplacement = () => {
    if (!candidate || !review) return;
    const result = replaceLayerData(layer.id, candidate, resolutions);
    if (!result.ok) {
      setErrorMessage(result.message);
      return;
    }
    onClose();
  };

  return (
    <DialogShell
      className="new-map-dialog replace-layer-data-dialog"
      closeOnEscape={!busy}
      initialFocusRef={chooseButtonRef}
      onClose={cancelWorkflow}
      returnFocusTo={returnFocusTo}
      titleId={titleId}
    >
      <p className="eyebrow">Local data replacement</p>
      <h2 id={titleId}>Replace data for {layer.name}</h2>
      <p>
        The layer’s name, order, visibility, styling, labels, popups, filters, joins, and map view
        stay in place wherever they remain valid.
      </p>
      <input
        accept=".csv,.xlsx,.geojson,.json,.kml,.kmz,.zip"
        aria-label="Choose replacement data file"
        className="visually-hidden"
        onChange={handleFileInput}
        ref={inputRef}
        type="file"
      />
      <div className="replace-file-choice">
        <button
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          ref={chooseButtonRef}
          type="button"
        >
          {candidate ? 'Choose another file' : 'Choose replacement file'}
        </button>
        {candidate ? (
          <span title={candidate.provenance.originalFileName}>
            {candidate.provenance.originalFileName}
          </span>
        ) : null}
      </div>
      {busy ? <p role="status">{status}</p> : null}
      {review && candidate ? (
        <div className="replacement-review">
          <section aria-labelledby={`${titleId}-compatibility`}>
            <h3 id={`${titleId}-compatibility`}>Compatibility</h3>
            <dl className="replacement-summary">
              <div>
                <dt>Features</dt>
                <dd>
                  {source.featureCount.toLocaleString()} →{' '}
                  {candidate.data.features.length.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt>Geometry</dt>
                <dd>
                  {familyLabel(review.geometry.currentFamilies)} →{' '}
                  {familyLabel(review.geometry.replacementFamilies)}
                </dd>
              </div>
              <div>
                <dt>Fields kept</dt>
                <dd>{review.sharedFields.length.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Fields added</dt>
                <dd>{review.addedFields.length.toLocaleString()}</dd>
              </div>
              <div>
                <dt>Fields removed</dt>
                <dd>{review.removedFields.length.toLocaleString()}</dd>
              </div>
            </dl>
            {!review.geometry.compatible ? (
              <p className="import-error" role="alert">
                This replacement is blocked because its geometry family does not match the current
                layer.
              </p>
            ) : null}
            {review.addedFields.length > 0 ? (
              <p className="replacement-information">
                New fields: {review.addedFields.map((field) => field.name).join(', ')}
              </p>
            ) : null}
            {review.removedFields.length > 0 ? (
              <p className="replacement-information">
                Removed fields: {review.removedFields.map((field) => field.name).join(', ')}
              </p>
            ) : null}
            {review.typeChanges.length > 0 ? (
              <ul className="replacement-warnings">
                {review.typeChanges.map((change) => (
                  <li key={change.field}>
                    {change.field}: {fieldTypeLabel(change.currentType)} →{' '}
                    {fieldTypeLabel(change.replacementType)}
                    {change.referenced ? ' (used by layer settings)' : ''}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>
          {review.requiredResolutions.length > 0 ? (
            <section aria-labelledby={`${titleId}-fields`}>
              <h3 id={`${titleId}-fields`}>Repair field references</h3>
              <p>
                Choose a compatible replacement field, or deliberately remove the affected settings.
              </p>
              <div className="replacement-mappings">
                {review.requiredResolutions.map((dependency) => {
                  const resolution = resolutions[dependency.field];
                  const value = resolution
                    ? resolution.kind === 'map'
                      ? `map:${resolution.replacementField}`
                      : 'remove-settings'
                    : '';
                  return (
                    <div className="replacement-mapping" key={dependency.field}>
                      <label htmlFor={`${titleId}-${dependency.field}`}>
                        <strong>{dependency.field}</strong>
                        <span>
                          Used by: {dependencyContext(dependency.areas, dependency.joinNames)}
                        </span>
                        <select
                          aria-describedby={`${titleId}-${dependency.field}-help`}
                          id={`${titleId}-${dependency.field}`}
                          onChange={(event) => {
                            const selected = event.currentTarget.value;
                            setResolution(
                              dependency.field,
                              selected === ''
                                ? null
                                : selected === 'remove-settings'
                                  ? { kind: 'remove-settings' }
                                  : { kind: 'map', replacementField: selected.slice(4) },
                            );
                          }}
                          value={value}
                        >
                          <option value="">Choose a resolution…</option>
                          {dependency.compatibleFields.map((field) => (
                            <option key={field.name} value={`map:${field.name}`}>
                              Map to {field.name} ({fieldTypeLabel(field.type)})
                            </option>
                          ))}
                          <option value="remove-settings">Remove affected settings</option>
                        </select>
                      </label>
                      <p id={`${titleId}-${dependency.field}-help`}>
                        {dependency.reason === 'missing'
                          ? 'The replacement file does not contain this referenced field.'
                          : 'The field still exists, but its new type is incompatible with these settings.'}
                        {dependency.suggestedField
                          ? ` Suggested match: ${dependency.suggestedField}.`
                          : ''}
                        {resolution?.kind === 'remove-settings'
                          ? ` Replacing will ${removalConsequence(dependency.areas)}.`
                          : ''}
                      </p>
                      {dependency.suggestedField && !resolution ? (
                        <button
                          onClick={() =>
                            setResolution(dependency.field, {
                              kind: 'map',
                              replacementField: dependency.suggestedField!,
                            })
                          }
                          type="button"
                        >
                          Use suggested field
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </section>
          ) : (
            <p className="replacement-compatible" role="status">
              Existing layer settings are compatible. No field mapping is required.
            </p>
          )}
        </div>
      ) : null}
      {errorMessage ? (
        <p className="import-error" role="alert">
          {errorMessage}
        </p>
      ) : null}
      <div className="new-map-dialog-actions">
        <button disabled={busy} onClick={cancelWorkflow} type="button">
          Cancel
        </button>
        <button
          className="primary-action"
          disabled={!candidate || !review?.geometry.compatible || !allResolved || busy}
          onClick={confirmReplacement}
          type="button"
        >
          Replace data
        </button>
      </div>
    </DialogShell>
  );
}
